import cn from 'classnames';
import React from 'react';
import { DetailsInfo, ICard } from '../../../../types/Card';
import { DH } from '../../../../utils/DataHook';
import { t } from '../../../../utils/i18n';
import { Icon } from '../../elements';
import { MarkCardPrimary } from '../../elements/MarkCardPrimary';
import s from '../styles.scss';
import { stringifyExpirationDate } from '../../../../utils/expirationUtils';

interface RowHeaderProps extends Omit<ICard, DetailsInfo> {
  creditCardId: string;
  isExpanded: boolean;
  isExpired: boolean;
  rtl: boolean;
}

export const TableHeader: React.FC = () => (
  <header className={s.header}>
    <div className={s.cardNumber} data-hook={DH.DesktopTableHeader.Card}>
      {t('app.table-header.credit-card')}
    </div>
    <div className={s.cardDate} data-hook={DH.DesktopTableHeader.Expire}>
      {t('app.table-header.expire-card')}
    </div>
  </header>
);
TableHeader.displayName = 'TableHeader';

export const Container: React.FC<React.PropsWithChildren<{}>> = (props) => (
  <ul className={s.listContainer}>{props.children}</ul>
);
Container.displayName = 'Container';

export const Row: React.FC<
  React.PropsWithChildren<{
    isExpanded: boolean;
    dataHook?: string;
  }>
> = (props) => (
  <li className={s.creditCardRow} aria-expanded={props.isExpanded} data-hook={props.dataHook}>
    {props.children}
  </li>
);
Row.displayName = 'Row';

export const RowHeader: React.FC<RowHeaderProps> = (props) => {
  const { network, lastFourDigits, expiration, isExpanded, primary, creditCardId, isExpired, rtl } = props;

  return (
    <div data-hook={DH.CardRow.Header} className={cn(s.row, s.center)}>
      <div
        className={cn({
          [s.cardNumber]: true,
          [s.cardNumberRTL]: rtl,
        })}
        data-hook={DH.CardRow.Number}
      >
        <div className={s.icon}>
          <Icon cardNetwork={network} />
        </div>

        <span
          className={cn({
            [s.rtm]: rtl,
          })}
        >
          {t(network ? `network.${network}` : `app.table-header.credit-card`)} ({lastFourDigits})
        </span>
      </div>

      <div className={cn(s.cardDate, { [s.expired]: isExpired })} data-hook={DH.CardRow.ExpireDate}>
        {stringifyExpirationDate(expiration)} {isExpired && `(${t('app.card.expired')})`}
      </div>

      <MarkCardPrimary
        cardId={creditCardId}
        isExpired={isExpired}
        primary={primary}
        label={`${t(network ? `network.${network}` : `app.table-header.credit-card`)} ${lastFourDigits}`}
        rtl={rtl}
      />

      <div
        className={cn({
          [s.arrow]: true,
          [s.arrowRTL]: rtl,
          [s.expanded]: isExpanded,
        })}
      />
    </div>
  );
};
RowHeader.displayName = 'RowHeader';

export const CardContentContainer: React.FC<React.PropsWithChildren<{}>> = (props) => (
  <div className={s.cardContentContainer}>{props.children}</div>
);
CardContentContainer.displayName = 'CardContentContainer';
